import React, { useState, useEffect } from 'react';
import { Container, Card, ListGroup, Badge, Spinner, Alert, Image } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { getOrders, updateOrderStatus } from '../services/orderService';
import api from '../services/api';
import Layout from '../components/layout/Layout';
import './Orders.css';

const getStatusBadge = (status) => {
  if (!status) return 'secondary';

  switch (status.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'processing':
      return 'info';
    case 'ready_for_pickup':
      return 'primary';
    case 'completed':
    case 'delivered':
      return 'success';
    case 'shipped':
      return 'info';
    case 'cancelled':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getStatusDescription = (order) => {
  const status = (order.status || '').toLowerCase();
  const paymentStatus = (order.payment_status || '').toLowerCase();

  if (status === 'cancelled') {
    return 'This order was cancelled.';
  }

  if (paymentStatus !== 'paid') {
    return 'Waiting for payment confirmation.';
  }

  switch (status) {
    case 'pending':
      return 'Payment initialized. Waiting for confirmation.';
    case 'processing':
      return 'Payment confirmed. Seller is preparing your order.';
    case 'ready_for_pickup':
      return 'Your order is ready for pickup.';
    case 'completed':
    case 'delivered':
      return 'Order completed.';
    case 'shipped':
      return 'Order has been shipped.';
    default:
      return 'Order is being processed.';
  }
};

const Orders = ({ sellerView = false }) => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const endpoint = sellerView ? '/seller/orders/' : '/orders/';
        const response = await api.get(endpoint);
        setOrders(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to fetch orders. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [sellerView]);

  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, { status });
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status } : order
        )
      );
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status. Please try again.');
    }
  };

  const handleConfirmPickup = async (orderId) => {
    const pickupCode = window.prompt('Enter the pickup code you received:');
    if (!pickupCode) return;

    try {
      await api.post(`/orders/${orderId}/confirm-pickup/`, { pickup_code: pickupCode });

      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: 'completed' } : order
        )
      );
    } catch (err) {
      console.error('Error confirming pickup:', err);
      setError('Failed to confirm pickup. Please check the code and try again.');
    }
  };

  const renderOrderItem = (item) => (
    <ListGroup.Item key={item.id} className="d-flex align-items-center">
      <Image src={item.image} rounded className="me-3" style={{ width: '60px' }} />
      <div className="flex-grow-1">
        <strong>{item.name}</strong>
        <div className="text-muted">Qty: {item.quantity}</div>
        {sellerView && (
          <div className="mt-1">
            <small>Buyer: {item.buyer_name || 'N/A'}</small>
          </div>
        )}
      </div>
      <div className="text-end">
        <strong>GH₵{(item.price * item.quantity).toFixed(2)}</strong>
      </div>
    </ListGroup.Item>
  );

  if (loading) {
    return (
      <Layout>
        <Container className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading orders...</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container className="py-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>{sellerView ? 'Orders Received' : 'My Orders'}</h2>
          {sellerView && (
            <div>
              <span className="me-2">Status:</span>
              <select className="form-select d-inline-block w-auto">
                <option>All Orders</option>
                <option>Pending</option>
                <option>Shipped</option>
                <option>Delivered</option>
                <option>Cancelled</option>
              </select>
            </div>
          )}
        </div>
        {error && <Alert variant="warning">{error}</Alert>}
        {orders.length > 0 ? (
          orders.map(order => (
            <Card className="order-card mb-4" key={order.id}>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Order #{order.id}</h5>
                  <small className="text-muted">Placed on {new Date(order.created_at).toLocaleString()}</small>
                </div>
                <Badge bg={getStatusBadge(order.status)} pill>
                  {order.status}
                </Badge>
              </Card.Header>
              <Card.Body>
  <ListGroup variant="flush">
    {order.items && order.items.map(renderOrderItem)}
  </ListGroup>
  <div className="mt-3">
    <div className="row">
      <div className="col-md-6">
        <div><strong>Order Date:</strong> {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}</div>
        <div><strong>Payment Method:</strong> {order.payment_method || 'N/A'}</div>
        <div><strong>Payment Status:</strong> 
          <Badge bg={order.payment_status === 'paid' ? 'success' : 'warning'} className="ms-2">
            {order.payment_status || 'Pending'}
          </Badge>
        </div>
      </div>
      <div className="col-md-6">
        <div><strong>Delivery Status:</strong> 
          <Badge bg={getStatusBadge(order.status)} className="ms-2">
            {order.status || 'Processing'}
          </Badge>
        </div>
        {!sellerView && (
          <>
            <div><strong>Delivery Address:</strong> {order.delivery_address || 'N/A'}</div>
            <div className="mt-2 text-muted">
              {getStatusDescription(order)}
            </div>
          </>
        )}
        {sellerView && order.buyer && (
          <div><strong>Buyer:</strong> {order.buyer.name || order.buyer.email || 'N/A'}</div>
        )}
      </div>
    </div>
  </div>
</Card.Body>
<Card.Footer className="d-flex justify-content-between align-items-center">
  <strong>Total: GH₵{!isNaN(parseFloat(order.total)) ? parseFloat(order.total).toFixed(2) : order.total}</strong>
  <div>
    <a href={`/order-confirmation/${order.id}`} className="btn btn-outline-primary btn-sm me-2">
      View Details
    </a>

    {/* Buyer: Confirm Pickup */}
    {!sellerView &&
      order.payment_status === 'paid' &&
      (order.status === 'processing' || order.status === 'ready_for_pickup') && (
        <button
          className="btn btn-outline-success btn-sm me-2"
          onClick={() => handleConfirmPickup(order.id)}
        >
          Confirm Pickup
        </button>
      )}

    {/* Seller: existing action */}
    {sellerView && (
      <button 
        className="btn btn-outline-success btn-sm"
        onClick={() => handleStatusUpdate(order.id, 'shipped')}
        disabled={order.status !== 'pending'}
      >
        Mark as Shipped
      </button>
    )}
  </div>
</Card.Footer>
            </Card>
          ))
        ) : (
          <Card>
            <Card.Body>
              <p>You have no orders yet.</p>
            </Card.Body>
          </Card>
        )}
      </Container>
    </Layout>
  );
};

export default Orders;
